/**
 * Utility functions for checking Commanders Act CMP cookie consent
 * Commanders Act runs in unmanaged mode for Covea
 * 
*/
const VIDEO_CONSENT_IDS = ['99'] as const;

interface ConsentStatus {
    status?: 'on' | 'off';
}

interface CactResponse {
    consent?: {
        categories: Record<string, 'on' | 'off' | ConsentStatus>;
        vendors: Record<string, 'on' | 'off' | ConsentStatus>;
    };
    categories?: Record<string, 'on' | 'off' | ConsentStatus>;
    vendors?: Record<string, 'on' | 'off' | ConsentStatus>;
}

declare global {
    interface Window {
        cact?: (event: string, callback: (data: CactResponse) => void) => void;
        tC?: { privacy?: { showPrivacyCenter?: () => void } };
    }
}

export const hasVideoCookieConsent = (timeoutMs = 2000): Promise<boolean> => {
    return new Promise((resolve) => {
        if (typeof window === 'undefined' || !window.cact) {
            return resolve(false);
        }

        const timer = setTimeout(() => {
            console.warn('Consent check timed out after', timeoutMs, 'ms');
            resolve(false);
        }, timeoutMs);

        window.cact('consent.get', (response) => {
            clearTimeout(timer);

            const data = response?.consent ?? response;
            const categories = data?.categories;
            const vendors = data?.vendors;

            if (!categories) {
                console.warn('CMP Debug: No categories found.');
                return resolve(false);
            }

            if (!vendors) {
                console.warn('CMP Debug: No vendors found.');
                return resolve(false);
            }

            const isAccepted = VIDEO_CONSENT_IDS.every(id => {
                const categoriesVal = categories[id];
                const vendorsVal = vendors[id];
                const categoriesStatus = (typeof categoriesVal === 'object') ? categoriesVal.status : categoriesVal;
                const vendorsStatus = (typeof vendorsVal === 'object') ? vendorsVal.status : vendorsVal;
                return categoriesStatus === 'on' && vendorsStatus === 'on';
            });

            resolve(isAccepted);
        });
    });
};


export const checkVideoCookieConsent = async (): Promise<boolean> => {
    try {
        return await hasVideoCookieConsent();
    } catch (error) {
        console.warn('Consent check failed:', error);
        return false;
    }
};

/**
 * Opens the Commanders Act consent preferences dialog - privacy center
 * Allows users to update their cookie preferences
 */
export const openConsentPreferences = (): void => {
    const showPrivacyCenter = window.tC?.privacy?.showPrivacyCenter;

    if (showPrivacyCenter) {
        try {
            showPrivacyCenter();
        } catch (error) {
            console.error('Error opening CMP:', error);
        }
    } else {
        console.warn('Commanders Act CMP showPrivacyCenter not found');
    }
};